import { useState } from "react";
import { logger } from "../../observability/LoggerFactory";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import type { SiteListItem } from "./useSiteList";
import { getCredentialPassword } from "./GetCredentialPassword";
import { PasswordCell } from "./PasswordCell";
import type { PasswordCellState } from "./SiteDataGrid";

export function CredentialsSubTable(props: {
  credentials: NonNullable<SiteListItem["credentials"]>;
}) {
  const [passwordStates, setPasswordStates] = useState<Record<string, PasswordCellState>>({});

  const togglePassword = async (credentialId: string) => {
    const current = passwordStates[credentialId] ?? { kind: "hidden" };
    if (current.kind === "revealed" || current.kind === "error") {
      setPasswordStates((previous) => ({ ...previous, [credentialId]: { kind: "hidden" } }));
      return;
    }
    if (current.kind === "loading") {
      return;
    }
    setPasswordStates((previous) => ({ ...previous, [credentialId]: { kind: "loading" } }));
    try {
      const password = await getCredentialPassword(credentialId);
      setPasswordStates((previous) => ({ ...previous, [credentialId]: { kind: "revealed", value: password } }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Не удалось получить пароль";
      logger.error({ err: message, component: "CredentialsSubTable" }, "Failed to reveal credential password");
      setPasswordStates((previous) => ({ ...previous, [credentialId]: { kind: "error", message } }));
    }
  };

  return (
    <Box sx={{ py: 1, pl: 6 }}>
      <Typography variant="subtitle2" gutterBottom>
        Доступы
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Логин</TableCell>
            <TableCell>Пароль</TableCell>
            <TableCell>Blog ID</TableCell>
            <TableCell>Селлер</TableCell>
            <TableCell>Происхождение</TableCell>
            <TableCell>Article XML-RPC</TableCell>
            <TableCell>Article API</TableCell>
            <TableCell>Article wp-admin</TableCell>
            <TableCell>Homepage API</TableCell>
            <TableCell>Homepage wp-admin</TableCell>
            <TableCell>Link-removal API</TableCell>
            <TableCell>Link-removal XML-RPC</TableCell>
            <TableCell>Link-removal wp-admin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.credentials.map((cred) => (
            <TableRow key={cred.id}>
              <TableCell>{cred.login}</TableCell>
              <TableCell>
                <PasswordCell
                  state={passwordStates[cred.id] ?? { kind: "hidden" }}
                  onToggle={() => {
      void togglePassword(cred.id);
    }}
                />
              </TableCell>
              <TableCell>{cred.blogId ?? "—"}</TableCell>
              <TableCell>{cred.seller ?? "—"}</TableCell>
              <TableCell>
                {cred.isPluginCredential ? (
                  <Chip size="small" color="primary" label="Создан плагином" />
                ) : (
                  <Chip size="small" variant="outlined" label="Селлер" />
                )}
              </TableCell>
              <TableCell title={cred.articleXmlRpcErrorDescription ?? undefined}>{cred.articleXmlRpcStatus}</TableCell>
              <TableCell title={cred.articleApiErrorDescription ?? undefined}>{cred.articleApiStatus}</TableCell>
              <TableCell title={cred.articleWpAdminErrorDescription ?? undefined}>{cred.articleWpAdminStatus}</TableCell>
              <TableCell title={cred.homepageApiErrorDescription ?? undefined}>{cred.homepageApiStatus}</TableCell>
              <TableCell title={cred.homepageWpAdminErrorDescription ?? undefined}>{cred.homepageWpAdminStatus}</TableCell>
              <TableCell>{cred.linkRemovalApiStatus}</TableCell>
              <TableCell>{cred.linkRemovalXmlRpcStatus}</TableCell>
              <TableCell>{cred.linkRemovalWpAdminStatus}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
